"use client";
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Search,
  Filter as FilterIcon,
  ArrowUpDown,
  Truck,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useFetchVehicles } from "../../../hooks/useFetchVehicle";
import { Vehicle } from "../../../types/Vehicle";
import LoadingComponent from "../../../components/form-components/LoadingComponent";

const useClickOutside = (
  ref: React.RefObject<HTMLDivElement>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

const VehiclesPage: React.FC = () => {
  const { vehicles, loading, error } = useFetchVehicles();
  const [selectedType, setSelectedType] = useState("All Vehicles");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Vehicle;
    direction: "asc" | "desc";
  }>({ key: "isVerified", direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const filterMenuRef = useRef<HTMLDivElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  useClickOutside(filterMenuRef, () => setIsFilterMenuOpen(false));
  useClickOutside(sortMenuRef, () => setIsSortMenuOpen(false));

  const vehicleTypes = useMemo(
    () => [
      "All Vehicles",
      ...Array.from(new Set(vehicles.map((v) => v.vehicleType))),
    ],
    [vehicles],
  );

  const filteredVehicles = useMemo(() => {
    let filtered = vehicles;
    if (selectedType !== "All Vehicles") {
      filtered = filtered.filter(
        (vehicle) => vehicle.vehicleType === selectedType,
      );
    }
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          (v.vehicleType || "").toLowerCase().includes(lowercasedFilter) ||
          (v.vehicleNumber || "").toLowerCase().includes(lowercasedFilter) ||
          (v.driverName || "").toLowerCase().includes(lowercasedFilter) ||
          (v.phoneNumber || "").includes(searchTerm) ||
          (v._id || "").toLowerCase().includes(lowercasedFilter),
      );
    }
    return filtered;
  }, [vehicles, selectedType, searchTerm]);

  const paginatedVehicles = useMemo(() => {
    const sortedVehicles = [...filteredVehicles].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        comparison = Number(aValue) - Number(bValue);
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    const startIndex = (currentPage - 1) * 10;
    return sortedVehicles.slice(startIndex, startIndex + 10);
  }, [filteredVehicles, currentPage, sortConfig]);

  const totalPages = Math.ceil(filteredVehicles.length / 10);

  const handleSort = (key: keyof Vehicle) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setIsSortMenuOpen(false);
  };

  const handleFilter = (type: string) => {
    setSelectedType(type);
    setCurrentPage(1);
    setIsFilterMenuOpen(false);
  };

  if (loading) return <LoadingComponent />;

  return (
    <div className="p-4 min-h-screen animate-fadeIn">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <VehicleCard
          title="All Vehicles"
          count={vehicles.length}
          icon={<Truck className="text-blue-500" size={28} />}
          color="border-blue-500"
        />
        <VehicleCard
          title="Verified"
          count={vehicles.filter((vehicle) => vehicle.isVerified).length}
          icon={<CheckCircle className="text-green-500" size={28} />}
          color="border-green-500"
        />
        <VehicleCard
          title="Unverified"
          count={vehicles.filter((vehicle) => !vehicle.isVerified).length}
          icon={<XCircle className="text-red-500" size={28} />}
          color="border-red-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow p-4 animate-slideUp">
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center">
            <AlertCircle className="mr-2" size={20} />
            <span>Error: {error}</span>
          </div>
        )}
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <div className="relative flex-grow sm:flex-grow-0">
            <input
              type="text"
              placeholder="Search by ID, type, number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-full sm:w-64"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <div className="flex items-center gap-2">
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <FilterIcon size={20} />
              </button>
              {isFilterMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                  {vehicleTypes.map((type) => (
                    <button
                      key={type}
                      onClick={() => handleFilter(type)}
                      className={`block w-full text-left px-4 py-2 text-sm ${
                        selectedType === type ? "bg-gray-100" : ""
                      } hover:bg-gray-100`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={() => setIsSortMenuOpen((prev) => !prev)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <ArrowUpDown size={20} />
              </button>
              {isSortMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                  <button
                    onClick={() => handleSort("vehicleType")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Type
                  </button>
                  <button
                    onClick={() => handleSort("driverName")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Driver
                  </button>
                  <button
                    onClick={() => handleSort("isVerified")}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Sort by Verified
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <VehicleTable vehicles={paginatedVehicles} />
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalEntries={filteredVehicles.length}
        />
      </div>
    </div>
  );
};

const VehicleCard: React.FC<{
  title: string;
  count: number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, count, icon, color }) => (
  <div
    className={`bg-white rounded-lg shadow p-4 flex items-center space-x-4 transition-all duration-300 transform hover:scale-105 hover:shadow-lg border-l-4 ${color}`}
  >
    <div className="flex-shrink-0">{icon}</div>
    <div>
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <h2 className="text-2xl font-bold text-gray-800">{count}</h2>
    </div>
  </div>
);

const VehicleTable: React.FC<{ vehicles: Vehicle[] }> = ({ vehicles }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b bg-gray-50">
          <th className="text-left p-3 font-semibold text-gray-600">
            Vehicle Type
          </th>
          <th className="text-left p-3 font-semibold text-gray-600">
            Vehicle Number
          </th>
          <th className="text-left p-3 font-semibold text-gray-600">
            Driver Name
          </th>
          <th className="text-left p-3 font-semibold text-gray-600">
            Driver Phone
          </th>
          <th className="text-left p-3 font-semibold text-gray-600">
            Is Verified
          </th>
          <th className="text-center p-3 font-semibold text-gray-600">View</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.length > 0 ? (
          vehicles.map((vehicle) => (
            <tr
              key={vehicle._id}
              className={`border-b animate-fadeIn transition-colors duration-200 ${
                vehicle.isVerified
                  ? "bg-green-50/50 hover:bg-green-100/60"
                  : "bg-red-50/50 hover:bg-red-100/60"
              }`}
            >
              <td className="p-3">{vehicle.vehicleType}</td>
              <td className="p-3">{vehicle.vehicleNumber}</td>
              <td className="p-3">{vehicle.driverName}</td>
              <td className="p-3">{vehicle.phoneNumber}</td>
              <td className="p-3">
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    vehicle.isVerified
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {vehicle.isVerified ? "Yes" : "No"}
                </span>
              </td>
              <td className="p-3 flex justify-center items-center">
                <Link
                  to={`/vehicles/${vehicle._id}`}
                  className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200/70 hover:bg-blue-100 text-gray-600 hover:text-blue-600 transition-all duration-200 ease-in-out transform hover:scale-110"
                  aria-label="View details"
                >
                  <Eye size={18} />
                </Link>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={6} className="text-center p-4 text-gray-500">
              No vehicles found.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalEntries: number;
}> = ({ currentPage, totalPages, onPageChange, totalEntries }) => (
  <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
    <p className="text-sm text-gray-600 mb-2 sm:mb-0">
      Showing {(currentPage - 1) * 10 + 1} to{" "}
      {Math.min(currentPage * 10, totalEntries)} of {totalEntries} entries
    </p>
    <div className="flex space-x-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50"
      >
        <ChevronLeft size={18} />
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`px-3 py-1 rounded border ${
            currentPage === page
              ? "bg-[#3c0b0b] text-white"
              : "hover:bg-gray-100"
          }`}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 rounded border hover:bg-gray-100 disabled:opacity-50"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  </div>
);

export default VehiclesPage;
